import { useEffect, useState } from 'react';
import { BookOpen, IndianRupee } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { villageUx } from '../lib/api';
import KhataManagement from '../components/KhataManagement';
import MyKhataQrButton from '../components/MyKhataQrButton';
import { fmtStamp } from '../components/LiveClock';
import { toast } from 'sonner';
import { payKhataOnline } from '../lib/khataPay';
import { apiError } from '../components/EmailAuth';

export default function MyKhata() {
  const { user, loading } = useAuth();
  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink/60">Loading…</div>;
  if (!user) {
    return (
      <div data-testid="khata-signin-gate" className="max-w-md mx-auto px-6 py-16 text-center">
        <BookOpen className="w-12 h-12 mx-auto text-ink/30"/>
        <h1 className="font-heading font-black text-2xl mt-3">Sign in to see your Khata</h1>
      </div>
    );
  }
  if (user.role === 'owner' || user.role === 'admin') {
    return <div className="max-w-7xl mx-auto px-4 md:px-6 py-6" data-testid="khata-owner-view"><KhataManagement/></div>;
  }
  return <CustomerKhata/>;
}

function CustomerKhata() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(null);
  const load = () => villageUx.myKhata().then(setData).catch(() => setData({ stores: [], total_due: 0 }));
  useEffect(() => { load(); }, []);
  const pay = async (s) => {
    setPaying(s.store_id);
    try {
      const r = await payKhataOnline({ storeId: s.store_id, storeName: s.store_name, amount: s.balance, user });
      toast.success(`Paid! ${r.row?.txn_serial || ''} · Due now ₹${r.due}`);
      load();
    } catch (e) { if (e?.message !== 'Payment cancelled') toast.error(apiError(e)); }
    finally { setPaying(null); }
  };
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6" data-testid="khata-customer-view">
      <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/60">Mera Khata</div>
      <h1 className="font-heading font-black text-3xl">My Udhaar</h1>
      <div className="mt-4 rounded-2xl bg-ink text-white p-5 flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-white/60">Total due to shops</div>
          <div data-testid="khata-total-due" className="font-heading font-black text-4xl tabular-nums mt-1">₹{(data?.total_due ?? 0).toFixed(0)}</div>
        </div>
        <MyKhataQrButton/>
      </div>
      {data && data.stores.length === 0 && (
        <div data-testid="khata-empty" className="mt-6 text-center text-ink/60 text-sm">No udhaar yet. Show your Khata QR at the shop counter to start.</div>
      )}
      <div className="mt-4 grid gap-3">
        {(data?.stores || []).map((s) => (
          <div key={s.store_id} data-testid={`khata-store-${s.store_id}`} className="rounded-2xl border-2 border-ink/10 bg-white p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-black">{s.store_name}</div>
              <div className={`font-heading font-black text-xl tabular-nums ${s.balance > 0 ? 'text-[#B71C1C]' : 'text-[#1B5E20]'}`}>
                <IndianRupee className="w-4 h-4 inline -mt-1"/>{s.balance.toFixed(0)}
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-ink/50">Last entry {fmtStamp(s.last_txn_at)}</div>
              {s.balance > 0 && (
                <button data-testid={`khata-pay-${s.store_id}`} onClick={() => pay(s)} disabled={paying === s.store_id}
                  className="btn-primary !py-1.5 !px-3 text-xs !bg-[#1B5E20] !border-[#1B5E20]">
                  {paying === s.store_id ? 'Opening…' : `Pay ₹${s.balance.toFixed(0)} online`}
                </button>
              )}
            </div>
            <ul className="mt-2 divide-y divide-ink/5 text-sm">
              {s.entries.map((e, i) => (
                <li key={i} className="py-1.5 flex items-center gap-2">
                  <span className={`text-[10px] font-black uppercase w-14 ${e.type === 'debit' ? 'text-[#B71C1C]' : 'text-[#1B5E20]'}`}>{e.type === 'debit' ? 'Udhaar' : 'Paid'}</span>
                  <span className="flex-1 min-w-0 truncate">{e.note || '—'}{e.txn_serial && <span className="block font-mono text-[10px] text-ink/50">{e.txn_serial}</span>}</span>
                  <span className="text-[11px] text-ink/50 hidden sm:inline">{fmtStamp(e.created_at)}</span>
                  <span className="font-bold tabular-nums">₹{e.amount}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
